import {
  formatDayMonth,
  formatMediumDate,
  formatShortDate,
  formatShortDateNoYear,
  formatSpan,
  formatTime,
  formatWeekdayDay,
  getCalendarParts,
  spansDays,
} from "@/lib/format-date";

/** A single instance of a multi-day or recurring offering. */
export type OfferingSession = {
  id: string;
  starts_at: string; // ISO
  ends_at: string | null;
  position: number;
};

/** What the editor sends to the server — id is optional for new rows. */
export type OfferingSessionInput = {
  id?: string;
  starts_at: string;
  ends_at: string | null;
  position: number;
};

const WEEKDAY_PLURALS: Record<number, string> = {
  0: "Sundays",
  1: "Mondays",
  2: "Tuesdays",
  3: "Wednesdays",
  4: "Thursdays",
  5: "Fridays",
  6: "Saturdays",
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Render a sessions list as a single human string for the public page header
 * and email confirmations. Falls back gracefully across the common shapes:
 *
 *   - 1 session            → "Saturday 12 May 2026, 10:00–13:00"
 *   - 1 session, spanning  → "Friday 15 May, 16:00 – Sunday 17 May 2026, 14:00"
 *   - 2 contiguous days    → "Sat–Sun 11–12 May 2026, 10:00–13:00"
 *   - weekly cadence       → "6 Tuesdays from 12 May 2026, 18:00–20:00"
 *   - other                → "5 sessions, starting 12 May 2026"
 *
 * `timeZone` is the offering's stored timezone (e.g. "Africa/Johannesburg")
 * and is required for stable SSR — formatting in the runtime's local zone
 * produces hydration mismatches.
 */
export function formatSchedule(
  sessions: OfferingSession[],
  timeZone?: string | null
): string {
  if (sessions.length === 0) return "Date TBA";

  const sorted = [...sessions].sort(
    (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
  );

  const first = sorted[0].starts_at;
  const firstEnd = sorted[0].ends_at;

  if (sorted.length === 1) {
    return formatSpan(first, firstEnd, timeZone, "long");
  }

  // A residential block (Fri 16:00 → Sun 14:00) has no daily time to share
  // and no "consecutive days" reading, so the shape heuristics below don't
  // apply. Fall straight through to the plain count.
  if (sorted.some((s) => spansDays(s.starts_at, s.ends_at, timeZone))) {
    return `${sorted.length} sessions, starting ${formatMediumDate(first, timeZone)}`;
  }

  // Same start time across all sessions? Useful for both contiguous-day and weekly.
  const firstTime = formatTime(first, timeZone);
  const sharedTime = sorted.every(
    (s) => formatTime(s.starts_at, timeZone) === firstTime
  );
  const firstEndTime = firstEnd ? formatTime(firstEnd, timeZone) : null;
  const sharedEnd =
    firstEndTime != null &&
    sorted.every((s) => {
      if (!s.ends_at) return false;
      return formatTime(s.ends_at, timeZone) === firstEndTime;
    });
  const timeRange =
    sharedTime && firstEndTime && sharedEnd
      ? `, ${firstTime}–${firstEndTime}`
      : sharedTime
        ? `, ${firstTime}`
        : "";

  // Contiguous-day weekend: 2–3 sessions on consecutive days.
  if (sorted.length <= 3 && areConsecutiveDays(sorted, timeZone)) {
    const last = sorted[sorted.length - 1].starts_at;
    const firstParts = getCalendarParts(first, timeZone);
    const lastParts = getCalendarParts(last, timeZone);
    const sameMonth = firstParts.month === lastParts.month;
    const left = sameMonth
      ? formatWeekdayDay(first, timeZone)
      : formatShortDateNoYear(first, timeZone);
    const right = formatShortDate(last, timeZone);
    return `${left}–${right}${timeRange}`;
  }

  // Weekly cadence: every gap is 7 days and same weekday.
  if (sorted.length >= 2 && isWeeklyCadence(sorted, timeZone)) {
    const weekday = WEEKDAY_PLURALS[getCalendarParts(first, timeZone).weekday];
    return `${sorted.length} ${weekday} from ${formatMediumDate(first, timeZone)}${timeRange}`;
  }

  return `${sorted.length} sessions, starting ${formatMediumDate(first, timeZone)}${timeRange}`;
}

function areConsecutiveDays(
  sessions: OfferingSession[],
  timeZone?: string | null
): boolean {
  for (let i = 1; i < sessions.length; i++) {
    const prev = startOfDayInTZ(sessions[i - 1].starts_at, timeZone);
    const curr = startOfDayInTZ(sessions[i].starts_at, timeZone);
    if (curr - prev !== ONE_DAY_MS) return false;
  }
  return true;
}

function isWeeklyCadence(
  sessions: OfferingSession[],
  timeZone?: string | null
): boolean {
  const firstDow = getCalendarParts(sessions[0].starts_at, timeZone).weekday;
  for (let i = 1; i < sessions.length; i++) {
    const prev = startOfDayInTZ(sessions[i - 1].starts_at, timeZone);
    const curr = startOfDayInTZ(sessions[i].starts_at, timeZone);
    const days = Math.round((curr - prev) / ONE_DAY_MS);
    if (days !== 7) return false;
    if (getCalendarParts(sessions[i].starts_at, timeZone).weekday !== firstDow)
      return false;
  }
  return true;
}

/**
 * Returns the milliseconds-since-epoch for the start-of-day-in-TZ of an
 * instant. We only use the result to subtract from another start-of-day
 * to count whole days, so an off-by-one DST hour is fine — the day-difference
 * is what matters.
 */
function startOfDayInTZ(value: string, timeZone?: string | null): number {
  const parts = getCalendarParts(value, timeZone);
  // Reconstruct as if it were midnight UTC of that calendar date. This
  // matches how the previous Date.setHours(0,0,0,0) path treated it for
  // counting purposes.
  return Date.UTC(parts.year, parts.month - 1, parts.day);
}

/** Compact label for a single session, used in expandable lists and emails.
 *  A session that crosses days reads "Fri 15 May, 16:00 – Sun 17 May 2026, 14:00". */
export function formatSessionLine(
  session: OfferingSession,
  timeZone?: string | null
): string {
  return formatSpan(session.starts_at, session.ends_at, timeZone, "short");
}

// Re-export for callers that wanted the old `format(d, "d MMM")` semantics.
export { formatDayMonth };
